import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { Card } from '@/design-system';
import type { Payment } from '@/entities/credit';
import { tenantApi, tenantKeys } from '@/entities/tenant';

import { browser } from '../browser';
import { BillingRegion } from '../components/BillingRegion';
import { PaymentReturnCard } from '../components/PaymentReturnCard';
import { PurchaseCard } from '../components/PurchaseCard';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { WalletCard } from '../components/WalletCard';
import { billingCopy, formatKeuringen } from '../copy';
import { describeBillingError } from '../errors';
import {
    useBuyInspections,
    useChooseSubscription,
    usePayArrears,
    useSubscription,
    useSubscriptionChange,
    useWallet,
} from '../hooks';
import { PAYMENT_RETURN_PARAM, returnedPayment } from '../payment-return';
import { TabColumn } from './TabColumn';

type SubscriptionChange =
    { kind: 'switch'; planCode: string } | { kind: 'cancel' } | { kind: 'resume' };

/**
 * Abonnement & tegoed: wat het bedrijf bij Smartkeur afneemt. Alleen voor de
 * eigenaar (`billing-tabs.ts`).
 *
 * Bovenaan wat er net gebeurde: de testmodus, de terugkeer van de betaalpagina
 * (`?betaling={uuid}`) en een tegoed dat bijna op is. Dan het abonnement
 * (`#abonnement`): kiezen in de proef, en daarna wisselen, opzeggen en - na een
 * mislukte incasso - opnieuw betalen. Daaronder het tegoed naast keuringen kopen
 * (`#keuringen-kopen`).
 *
 * Elke betaling gaat naar de betaalpagina van de provider en komt terug op
 * `/facturatie?betaling={uuid}`: dit tabblad, dat dan navraagt hoe hij afliep.
 */
export function PlanTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const returnedFromPayment = searchParams.has(PAYMENT_RETURN_PARAM);
    const [returnedUuid] = useState(() => returnedPayment(searchParams));

    const [checkoutProblem, setCheckoutProblem] = useState<string | null>(null);
    const [subscriptionNotice, setSubscriptionNotice] = useState<string | null>(null);

    const wallet = useWallet();
    const subscription = useSubscription(true);
    const choose = useChooseSubscription();
    const payArrears = usePayArrears();
    const buy = useBuyInspections();
    const change = useSubscriptionChange();
    const tenant = useQuery({ queryKey: tenantKeys.current, queryFn: () => tenantApi.current() });
    const priceList = useQuery({
        queryKey: tenantKeys.pricing,
        queryFn: () => tenantApi.pricing(),
    });

    const paying = choose.isPending || payArrears.isPending || buy.isPending;
    const subscriptionError = choose.error ?? payArrears.error ?? change.error;

    /** Naar de betaalpagina van de provider; de API gaf de terugkeerlink al mee. */
    function toCheckout(payment: Payment) {
        if (payment.checkoutUrl === null) {
            setCheckoutProblem(billingCopy.noCheckout);

            return;
        }

        browser.assign(payment.checkoutUrl);
    }

    function startPayment(open: () => Promise<Payment>) {
        setCheckoutProblem(null);
        setSubscriptionNotice(null);
        open().then(toCheckout, () => undefined);
    }

    function changeSubscription(next: SubscriptionChange) {
        setSubscriptionNotice(null);
        change.mutate(next, {
            onSuccess: (overview) => {
                const current = overview.subscription;

                if (next.kind === 'resume') {
                    setSubscriptionNotice(billingCopy.planResumed);
                } else if (
                    next.kind === 'switch' &&
                    current?.nextPlan &&
                    current.currentPeriodEnd
                ) {
                    setSubscriptionNotice(
                        billingCopy.planSwitched(
                            current.nextPlan.name,
                            new Intl.DateTimeFormat('nl-NL', { dateStyle: 'long' }).format(
                                new Date(current.currentPeriodEnd),
                            ),
                        ),
                    );
                }
            },
        });
    }

    function closePaymentReturn() {
        const next = new URLSearchParams(searchParams);

        next.delete(PAYMENT_RETURN_PARAM);
        setSearchParams(next, { replace: true });
    }

    /** Een betaallink gaat rechtstreeks naar zijn betaalroute; zonder link een nieuwe betaling. */
    function payAgain() {
        const link = subscription.data?.paymentLink;

        if (link) {
            browser.assign(link.url);
        } else {
            startPayment(() => payArrears.mutateAsync());
        }
    }

    function payNow() {
        const link = subscription.data?.paymentLink;

        if (link) {
            browser.assign(link.url);
        }
    }

    return (
        <TabColumn>
            {subscription.data?.paymentsTestMode ? (
                <p
                    role="status"
                    className="m-0 rounded-[8px] border border-[var(--warn-border)] bg-[var(--warn-tint)] px-3 py-[10px] text-[14px] font-semibold leading-[1.4] text-[var(--warn-text)]"
                >
                    {billingCopy.paymentsTestMode}
                </p>
            ) : null}

            {returnedFromPayment ? (
                <PaymentReturnCard paymentUuid={returnedUuid} onClose={closePaymentReturn} />
            ) : null}

            {wallet.data?.isLow ? (
                <Card variant="warn" padding="md" role="status">
                    <p className="m-0 text-[14px] leading-[1.5] text-[var(--warn-ink)]">
                        {billingCopy.lowBalance(
                            formatKeuringen(wallet.data.balance),
                            formatKeuringen(wallet.data.lowBalanceThreshold),
                        )}{' '}
                        {billingCopy.lowBalanceTopUp}
                    </p>
                </Card>
            ) : null}

            <BillingRegion title={billingCopy.planTitle} query={subscription}>
                {subscription.data ? (
                    <SubscriptionCard
                        overview={subscription.data}
                        priceList={priceList.data ?? null}
                        trial={tenant.data?.trial ?? null}
                        busy={paying || change.isPending}
                        error={
                            subscriptionError
                                ? describeBillingError(subscriptionError)
                                : checkoutProblem
                        }
                        notice={subscriptionNotice}
                        onChoose={(planCode) => startPayment(() => choose.mutateAsync(planCode))}
                        onPayAgain={payAgain}
                        onPayNow={payNow}
                        onSwitch={(planCode) => changeSubscription({ kind: 'switch', planCode })}
                        onCancel={() => changeSubscription({ kind: 'cancel' })}
                        onResume={() => changeSubscription({ kind: 'resume' })}
                    />
                ) : null}
            </BillingRegion>

            {/* Wat je hebt naast wat je erbij koopt. */}
            <div className="grid items-start gap-[14px] @min-[860px]:grid-cols-2">
                <BillingRegion title={billingCopy.walletTitle} query={wallet}>
                    {wallet.data ? <WalletCard wallet={wallet.data} /> : null}
                </BillingRegion>

                {subscription.data ? (
                    <PurchaseCard
                        purchase={subscription.data.purchase}
                        hasSubscription={subscription.data.state !== 'none'}
                        busy={paying}
                        error={buy.error ? describeBillingError(buy.error) : null}
                        onBuy={(quantity) => startPayment(() => buy.mutateAsync(quantity))}
                    />
                ) : null}
            </div>
        </TabColumn>
    );
}
